import moment from 'moment';

/**
 * Formats time strings consistently across all partial offer components
 * Prioritizes pre-formatted local times, falls back to UTC conversion
 * Handles Android timezone issues by providing more robust fallbacks
 */
export const formatPartialOfferTime = (
  timeStr: string | undefined,
  localTimeStr?: string | undefined,
  locationLat?: number,
  locationLon?: number
): string => {
  if (!timeStr) return '';

  if (localTimeStr && localTimeStr.trim() !== '') {
    return localTimeStr;
  }

  if (timeStr.includes('am') || timeStr.includes('pm') || 
      timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }

  try {
    const utcMoment = moment.utc(timeStr);
    
    if (!utcMoment.isValid()) {
      console.warn('Invalid time string:', timeStr);
      return timeStr;
    }

    const localTime = utcMoment.local().format('h:mm A');
    
    // Additional validation for Android edge cases
    if (localTime === 'Invalid date' || localTime.includes('Invalid')) {
      console.warn('Invalid local time conversion for:', timeStr);
      return timeStr;
    }

    return localTime;
  } catch (error) {
    console.error('Error formatting time:', error, 'for time string:', timeStr);
    return timeStr; // Return original string as fallback
  }
};

/**
 * Formats partial offer times specifically for display
 * Uses both offered times and local formatted times for consistency
 */
export const formatPartialOfferTimeRange = (
  startTime: string | undefined,
  endTime: string | undefined,
  startTimeLocal?: string | undefined,
  endTimeLocal?: string | undefined,
  locationLat?: number,
  locationLon?: number
): { formattedStartTime: string; formattedEndTime: string } => {
  return {
    formattedStartTime: formatPartialOfferTime(startTime, startTimeLocal, locationLat, locationLon),
    formattedEndTime: formatPartialOfferTime(endTime, endTimeLocal, locationLat, locationLon)
  };
};

/**
 * Debug function to log time formatting details
 * Useful for troubleshooting Android timezone issues
 */
export const debugTimeFormatting = (
  timeStr: string | undefined,
  localTimeStr?: string | undefined,
  context?: string
): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[TimeFormatting${context ? ` - ${context}` : ''}]`, {
      original: timeStr,
      local: localTimeStr,
      formatted: formatPartialOfferTime(timeStr, localTimeStr),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform
    });
  }
}; 